import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';
import { Button, type ButtonProps } from './Button';

const EMPTY_STATE_THEME = {
  iconBg: 'rgba(0, 0, 0, 0.35)',
  iconBorder: 'rgba(245, 236, 99, 0.35)',
  iconShadow: '0 0 14px rgba(245,236,99,0.18), inset 0 3px 10px rgba(245,236,99,0.18)',
  iconColor: '#F5EC63',
  title: '#E5E7EB',
  description: '#9CA3AF',
} as const;

export type EmptyStateProps = {
  icon?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  primaryAction?: { label: React.ReactNode } & Pick<ButtonProps, 'onClick'>;
  secondaryAction?: { label: React.ReactNode } & Pick<ButtonProps, 'onClick'>;
  className?: string;
};

export function EmptyState({
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  className,
}: EmptyStateProps) {
  const { t } = useTranslation();
  const displayTitle = title ?? t('emptyState.title');
  const displayDescription = description ?? (title ? undefined : t('emptyState.description'));

  return (
    <div className={cn('mx-auto flex max-w-[320px] flex-col items-center py-10 text-center', className)}>
      {icon ? (
        <div
          className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border"
          style={{
            background: EMPTY_STATE_THEME.iconBg,
            borderColor: EMPTY_STATE_THEME.iconBorder,
            boxShadow: EMPTY_STATE_THEME.iconShadow,
            color: EMPTY_STATE_THEME.iconColor,
          }}
        >
          {icon}
        </div>
      ) : null}
      <div className="text-sm font-semibold" style={{ color: EMPTY_STATE_THEME.title }}>{displayTitle}</div>
      {displayDescription ? <div className="mt-1 text-sm" style={{ color: EMPTY_STATE_THEME.description }}>{displayDescription}</div> : null}
      {(primaryAction || secondaryAction) && (
        <div className="mt-5 flex w-full flex-col gap-2">
          {primaryAction ? (
            <Button fullWidth onClick={primaryAction.onClick}>
              {primaryAction.label}
            </Button>
          ) : null}
          {secondaryAction ? (
            <Button fullWidth variant="secondary" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}
